import { ErrorMessage, useFormikContext } from 'formik';
import { Layer } from 'iconsax-react';
import { useMemo } from 'react';

import { Checkbox } from '@src/design-system/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@src/design-system/command';
import { CustomErrorMessage } from '@src/design-system/error-message';
import { cn } from '@src/design-system/helpers';
import { inputVariants } from '@src/design-system/input';
import { Label } from '@src/design-system/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@src/design-system/popover';
import type { TStage } from '@src/shared/types/stage';

type StageFormValues = {
  stages?: number[];
};

type ProgramStageMultiSelectProps = {
  stages: TStage[];
  label?: string;
  searchPlaceholder?: string;
};

export function ProgramStageMultiSelect({
  stages,
  label = 'Stage',
  searchPlaceholder = 'Search for stages',
}: ProgramStageMultiSelectProps) {
  const { values, setFieldValue, setFieldTouched, errors, touched } =
    useFormikContext<StageFormValues>();

  const selectedIds = useMemo(
    () => new Set(values.stages ?? []),
    [values.stages]
  );

  const isAllSelected =
    stages.length > 0 && stages.every((stage) => selectedIds.has(stage.id));

  const selectedStageNames = stages
    .filter((stage) => selectedIds.has(stage.id))
    .map((stage) => stage.name);

  const handleSelectAll = (checked: boolean) => {
    setFieldValue('stages', checked ? stages.map((stage) => stage.id) : []);
    setFieldTouched('stages', true, false);
  };

  const updateSelection = (stageId: number, checked: boolean) => {
    const next = new Set(selectedIds);

    if (checked) next.add(stageId);
    else next.delete(stageId);

    setFieldValue('stages', Array.from(next));
    setFieldTouched('stages', true, false);
  };

  const handleToggle = (stageId: number) => {
    updateSelection(stageId, !selectedIds.has(stageId));
  };

  return (
    <div>
      <Label>{label}</Label>
      <Popover modal>
        <PopoverTrigger
          type='button'
          {...(errors.stages && touched.stages && { 'data-error': 'error' })}
          className={cn(
            inputVariants({ variant: 'fill', size: 'lg' }),
            'mt-2 flex w-full cursor-pointer items-center justify-between text-sm'
          )}
        >
          <span className='flex items-center gap-2'>
            <Layer
              size={20}
              color='#584777'
            />
            {isAllSelected
              ? 'Selected Stages (All Stages)'
              : selectedStageNames.length > 0
                ? 'Selected Stages (' + selectedStageNames.join(', ') + ')'
                : 'Select Stages'}
          </span>
        </PopoverTrigger>
        <PopoverContent
          align='start'
          className='w-[var(--radix-popover-trigger-width)] p-0'
        >
          <Command>
            <CommandInput
              containerClassName='rounded-none mb-0 border-0 border-b-1 border-pale-border'
              placeholder={searchPlaceholder}
            />
            <CommandList className='max-h-[min(240px,var(--radix-popover-content-available-height,240px))]'>
              <CommandEmpty>No stages found</CommandEmpty>
              <CommandGroup className='p-0'>
                {stages.length > 0 && (
                  <CommandItem
                    value='All stages'
                    className='py-2 block overflow-hidden bg-white border-b border-primary-200 rounded-none'
                    data-selected={false}
                    onSelect={() => handleSelectAll(!isAllSelected)}
                  >
                    <div className='flex items-center gap-2'>
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={(checked) => {
                          handleSelectAll(Boolean(checked));
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <p className='text-sm font-normal text-gray-900'>
                        All Stages
                      </p>
                    </div>
                  </CommandItem>
                )}
                {stages.map((stage) => {
                  const isSelected = selectedIds.has(stage.id);

                  return (
                    <CommandItem
                      key={stage.id}
                      value={stage.name}
                      className='py-2 block overflow-hidden bg-white last:mb-0 border-b border-primary-200 last:border-b-0 rounded-none'
                      data-selected={false}
                      onSelect={() => handleToggle(stage.id)}
                    >
                      <div className='flex items-center gap-2'>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => {
                            updateSelection(stage.id, Boolean(checked));
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <p className='text-sm text-gray-900'>{stage.name}</p>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <ErrorMessage
        name='stages'
        component={CustomErrorMessage}
      />
    </div>
  );
}
